import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styles from './VideoSearch.module.scss';
import VideoList from '../../VideoList/VideoList';
import type { VideoListViewMode } from '../../VideoList/VideoList';
import { buildSearchInput, normalizeSearchParamTags, normalizeTagNameKey } from '../../../utils/video-search';
import { useVideoSearchInput } from '../../../hooks/useVideoSearchInput';
import PageHeader from '../../PageHeader/PageHeader';
import { useGlobalSearchSuggestions } from '../../../hooks/useGlobalSearchSuggestions';
import SearchDiscoveryContent from '../../Search/SearchDiscoveryContent';
import VideoSearchBar from '../../Search/VideoSearchBar';
import { useContinueWatching } from '../../../hooks/useContinueWatching';
import WatchMomentumPanel from '../../WatchMomentum/WatchMomentumPanel';
import { fetchCollections } from '../../../services/collection.service';
import type { VideoSearchFilters, VideoSearchSort } from '../../../services/video.service';
import type { HomeCollection } from '../../../types/home.types';
import VideoSearchHomeSections from './VideoSearchHomeSections';
import { createAnalyticsClientEventId, useAnalyticsService } from '../../../services/analytics.service';
import { ANALYTICS_EVENT_TYPES } from '../../../types/analytics.types';
import { trackMarketingConversion } from '../../../services/marketing.service';

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [20, 40] as const;
const SEARCH_DEBOUNCE_MS = 400;
const CONTEXTUAL_DISCOVERY_MIN_LENGTH = 2;
const STICKY_SEARCH_STORAGE_KEY = 'combattv:lastVideoSearch';
const COLLECTION_ID_PARAM = 'collectionId';
const COLLECTION_TITLE_PARAM = 'collection';
const VIEW_PARAM = 'view';
const PAGE_SIZE_PARAM = 'pageSize';
const SORT_PARAM = 'sort';
const EVENT_FILTER_PARAM = 'event';
const FIGHTER_FILTER_PARAM = 'fighter';
const VENUE_FILTER_PARAM = 'venue';
const TYPE_FILTER_PARAM = 'type';
const YEAR_FILTER_PARAM = 'year';
const DECADE_FILTER_PARAM = 'decade';
const DESKTOP_SETTINGS_BREAKPOINT = 1024;
const SETTINGS_DIALOG_MARGIN = 24;
const SETTINGS_DIALOG_MIN_WIDTH = 520;
const SETTINGS_DIALOG_MIN_HEIGHT = 360;
const SETTINGS_DIALOG_MAX_WIDTH = 840;
const SETTINGS_DIALOG_MAX_HEIGHT = 540;

interface SearchFilterState {
    event: string;
    fighter: string;
    venue: string;
    type: string;
    year: string;
    decade: string;
}

interface BrowseState {
    viewMode: VideoListViewMode;
    pageSize: number;
    sort: VideoSearchSort;
    filters: SearchFilterState;
}

interface StickyVideoSearchState {
    query: string;
    tags: string[];
}

interface SettingsDialogFrame {
    left: number;
    top: number;
    width: number;
    height: number;
}

type SettingsDialogPointerOperation =
    | { mode: 'drag'; startX: number; startY: number; frame: SettingsDialogFrame }
    | { mode: 'resize'; startX: number; startY: number; frame: SettingsDialogFrame };

type CollectionContextStatus = 'loading' | 'ready' | 'error';

interface CollectionContext {
    id?: string;
    title: string;
    description?: string;
    imageUrl?: string;
    tags: string[];
    status: CollectionContextStatus;
}

const getTrimmedParam = (params: URLSearchParams, key: string) => params.get(key)?.trim() ?? '';

const emptySearchFilters = (): SearchFilterState => ({
    event: '',
    fighter: '',
    venue: '',
    type: '',
    year: '',
    decade: ''
});

const sanitizeYearLikeFilter = (value: string) => {
    const trimmed = value.trim();
    return /^\d{4}$/.test(trimmed) ? trimmed : '';
};

const sanitizeFilterState = (filters: SearchFilterState): SearchFilterState => ({
    event: filters.event.trim(),
    fighter: filters.fighter.trim(),
    venue: filters.venue.trim(),
    type: filters.type.trim(),
    year: sanitizeYearLikeFilter(filters.year),
    decade: sanitizeYearLikeFilter(filters.decade)
});

const hasSearchFilterState = (filters: SearchFilterState) => (
    Object.values(sanitizeFilterState(filters)).some((value) => value.length > 0)
);

const isDesktopSettingsViewport = () => (
    typeof window !== 'undefined' && window.innerWidth >= DESKTOP_SETTINGS_BREAKPOINT
);

const createSettingsDialogFrame = (): SettingsDialogFrame | null => {
    if (!isDesktopSettingsViewport()) {
        return null;
    }

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const width = Math.min(SETTINGS_DIALOG_MAX_WIDTH, viewportWidth - SETTINGS_DIALOG_MARGIN * 2);
    const height = Math.min(SETTINGS_DIALOG_MAX_HEIGHT, viewportHeight - SETTINGS_DIALOG_MARGIN * 2);

    return {
        left: Math.round((viewportWidth - width) / 2),
        top: Math.max(SETTINGS_DIALOG_MARGIN, Math.min(72, Math.round(viewportHeight * 0.08))),
        width,
        height
    };
};

const clampSettingsDialogFrame = (frame: SettingsDialogFrame): SettingsDialogFrame | null => {
    if (!isDesktopSettingsViewport()) {
        return null;
    }

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const maxWidth = Math.max(SETTINGS_DIALOG_MIN_WIDTH, viewportWidth - SETTINGS_DIALOG_MARGIN * 2);
    const maxHeight = Math.max(SETTINGS_DIALOG_MIN_HEIGHT, viewportHeight - SETTINGS_DIALOG_MARGIN * 2);
    const width = Math.min(Math.max(frame.width, SETTINGS_DIALOG_MIN_WIDTH), Math.min(SETTINGS_DIALOG_MAX_WIDTH, maxWidth));
    const height = Math.min(Math.max(frame.height, SETTINGS_DIALOG_MIN_HEIGHT), Math.min(SETTINGS_DIALOG_MAX_HEIGHT, maxHeight));
    const maxLeft = Math.max(SETTINGS_DIALOG_MARGIN, viewportWidth - width - SETTINGS_DIALOG_MARGIN);
    const maxTop = Math.max(SETTINGS_DIALOG_MARGIN, viewportHeight - height - SETTINGS_DIALOG_MARGIN);

    return {
        left: Math.min(Math.max(frame.left, SETTINGS_DIALOG_MARGIN), maxLeft),
        top: Math.min(Math.max(frame.top, SETTINGS_DIALOG_MARGIN), maxTop),
        width,
        height
    };
};

const parseSortParam = (params: URLSearchParams): VideoSearchSort => (
    getTrimmedParam(params, SORT_PARAM) === 'oldest' ? 'oldest' : 'newest'
);

const parseViewModeParam = (params: URLSearchParams): VideoListViewMode => (
    getTrimmedParam(params, VIEW_PARAM) === 'compact' ? 'compact' : 'grid'
);

const parsePageSizeParam = (params: URLSearchParams) => {
    const pageSize = Number.parseInt(getTrimmedParam(params, PAGE_SIZE_PARAM), 10);
    return PAGE_SIZE_OPTIONS.includes(pageSize as typeof PAGE_SIZE_OPTIONS[number])
        ? pageSize
        : DEFAULT_PAGE_SIZE;
};

const parseFilterParams = (params: URLSearchParams): SearchFilterState => sanitizeFilterState({
    event: getTrimmedParam(params, EVENT_FILTER_PARAM),
    fighter: getTrimmedParam(params, FIGHTER_FILTER_PARAM),
    venue: getTrimmedParam(params, VENUE_FILTER_PARAM),
    type: getTrimmedParam(params, TYPE_FILTER_PARAM),
    year: getTrimmedParam(params, YEAR_FILTER_PARAM),
    decade: getTrimmedParam(params, DECADE_FILTER_PARAM)
});

const buildVideoSearchFilters = (sort: VideoSearchSort, filters: SearchFilterState): VideoSearchFilters => {
    const normalizedFilters = sanitizeFilterState(filters);
    return {
        sort,
        event: normalizedFilters.event,
        fighter: normalizedFilters.fighter,
        venue: normalizedFilters.venue,
        type: normalizedFilters.type,
        year: normalizedFilters.year ? Number.parseInt(normalizedFilters.year, 10) : undefined,
        decade: normalizedFilters.decade ? Number.parseInt(normalizedFilters.decade, 10) : undefined
    };
};

const appendBrowseParams = (params: URLSearchParams, browseState: BrowseState) => {
    if (browseState.viewMode !== 'grid') {
        params.set(VIEW_PARAM, browseState.viewMode);
    }

    if (browseState.pageSize !== DEFAULT_PAGE_SIZE) {
        params.set(PAGE_SIZE_PARAM, browseState.pageSize.toString());
    }

    if (browseState.sort !== 'newest') {
        params.set(SORT_PARAM, browseState.sort);
    }

    const normalizedFilters = sanitizeFilterState(browseState.filters);
    if (normalizedFilters.event) {
        params.set(EVENT_FILTER_PARAM, normalizedFilters.event);
    }
    if (normalizedFilters.fighter) {
        params.set(FIGHTER_FILTER_PARAM, normalizedFilters.fighter);
    }
    if (normalizedFilters.venue) {
        params.set(VENUE_FILTER_PARAM, normalizedFilters.venue);
    }
    if (normalizedFilters.type) {
        params.set(TYPE_FILTER_PARAM, normalizedFilters.type);
    }
    if (normalizedFilters.year) {
        params.set(YEAR_FILTER_PARAM, normalizedFilters.year);
    }
    if (normalizedFilters.decade) {
        params.set(DECADE_FILTER_PARAM, normalizedFilters.decade);
    }
};

const hasCollectionParams = (params: URLSearchParams) => (
    getTrimmedParam(params, COLLECTION_ID_PARAM).length > 0 ||
    getTrimmedParam(params, COLLECTION_TITLE_PARAM).length > 0
);

const buildFallbackCollectionContext = (
    params: URLSearchParams,
    tags: string[],
    status: CollectionContextStatus = 'loading'
): CollectionContext | null => {
    const collectionId = getTrimmedParam(params, COLLECTION_ID_PARAM);
    const collectionTitle = getTrimmedParam(params, COLLECTION_TITLE_PARAM);

    if (!collectionId && !collectionTitle) {
        return null;
    }

    return {
        id: collectionId || undefined,
        title: collectionTitle || 'Collection',
        tags,
        status
    };
};

const normalizeCollectionTitleKey = (value: string) => value.trim().toLowerCase();

const findCollection = (
    collections: HomeCollection[],
    fallback: CollectionContext
) => {
    const fallbackTitleKey = normalizeCollectionTitleKey(fallback.title);
    return collections.find((collection) => (
        (fallback.id && collection.id === fallback.id) ||
        normalizeCollectionTitleKey(collection.title) === fallbackTitleKey
    ));
};

const getTagKeySet = (tags: string[]) => new Set(
    tags
        .map(normalizeTagNameKey)
        .filter((tagKey) => tagKey.length > 0)
);

const shouldPreserveCollectionContext = (collectionContext: CollectionContext | null, tags: string[]) => {
    if (!collectionContext) {
        return false;
    }

    const collectionTagKeys = getTagKeySet(collectionContext.tags);
    if (collectionTagKeys.size === 0) {
        return true;
    }

    const activeTagKeys = getTagKeySet(tags);
    return Array.from(collectionTagKeys).every((tagKey) => activeTagKeys.has(tagKey));
};

const buildSearchParams = (
    query: string,
    tags: string[],
    collectionContext: CollectionContext | null = null,
    browseState: BrowseState = {
        viewMode: 'grid',
        pageSize: DEFAULT_PAGE_SIZE,
        sort: 'newest',
        filters: emptySearchFilters()
    }
) => {
    const nextParams = new URLSearchParams();
    const trimmedQuery = query.trim();
    const shouldKeepCollection = shouldPreserveCollectionContext(collectionContext, tags);

    if (shouldKeepCollection && collectionContext) {
        if (collectionContext.id) {
            nextParams.set(COLLECTION_ID_PARAM, collectionContext.id);
        }
        nextParams.set(COLLECTION_TITLE_PARAM, collectionContext.title);
    }

    if (trimmedQuery.length > 0) {
        nextParams.set('q', trimmedQuery);
    }

    tags
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0)
        .forEach((tag) => nextParams.append('tags', tag));

    appendBrowseParams(nextParams, browseState);

    return nextParams;
};

const hasSearchParams = (params: URLSearchParams) => (
    params.has('q') || params.has('tags') || hasSearchFilterState(parseFilterParams(params))
);

const readStickySearchState = (): StickyVideoSearchState | null => {
    try {
        const value = window.sessionStorage.getItem(STICKY_SEARCH_STORAGE_KEY);
        if (!value) {
            return null;
        }

        const parsed = JSON.parse(value) as Partial<StickyVideoSearchState>;
        const query = typeof parsed.query === 'string' ? parsed.query.trim() : '';
        const tags = Array.isArray(parsed.tags)
            ? normalizeSearchParamTags(parsed.tags.filter((tag): tag is string => typeof tag === 'string'))
            : [];

        if (!query && tags.length === 0) {
            return null;
        }

        return { query, tags };
    } catch (error) {
        return null;
    }
};

const writeStickySearchState = (query: string, tags: string[]) => {
    const normalizedQuery = query.trim();
    const normalizedTags = tags.map((tag) => tag.trim()).filter((tag) => tag.length > 0);

    try {
        if (!normalizedQuery && normalizedTags.length === 0) {
            window.sessionStorage.removeItem(STICKY_SEARCH_STORAGE_KEY);
            return;
        }

        window.sessionStorage.setItem(
            STICKY_SEARCH_STORAGE_KEY,
            JSON.stringify({ query: normalizedQuery, tags: normalizedTags })
        );
    } catch (error) {
        // Session storage can be unavailable in private/restricted contexts; search still works via URL state.
    }
};

const VideoSearch = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const stickySearchState = !hasSearchParams(searchParams) && !hasCollectionParams(searchParams)
        ? readStickySearchState()
        : null;
    const initialQuery = searchParams.get('q') ?? stickySearchState?.query ?? '';
    const initialTags = hasSearchParams(searchParams)
        ? normalizeSearchParamTags(searchParams.getAll('tags'))
        : stickySearchState?.tags ?? [];
    const {
        inputValue,
        setInputValue,
        parsedInput,
        parsedTagKey,
        displayTags,
        selectedTagKeys,
        showTagSkeletons,
        tagLoadFailed
    } = useVideoSearchInput({
        initialQuery,
        initialTags
    });

    const [collectionContext, setCollectionContext] = useState<CollectionContext | null>(() =>
        buildFallbackCollectionContext(searchParams, initialTags)
    );
    const [searchTerm, setSearchTerm] = useState(() => initialQuery.trim());
    const [activeSearchTags, setActiveSearchTags] = useState(() =>
        initialTags.map((tag) => tag.trim()).filter((tag) => tag.length > 0)
    );
    const [viewMode, setViewMode] = useState<VideoListViewMode>(() => parseViewModeParam(searchParams));
    const [pageSize, setPageSize] = useState(() => parsePageSizeParam(searchParams));
    const [sortOrder, setSortOrder] = useState<VideoSearchSort>(() => parseSortParam(searchParams));
    const [appliedFilters, setAppliedFilters] = useState<SearchFilterState>(() => parseFilterParams(searchParams));
    const [filterDraft, setFilterDraft] = useState<SearchFilterState>(() => parseFilterParams(searchParams));
    const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
    const [settingsDialogFrame, setSettingsDialogFrame] = useState<SettingsDialogFrame | null>(null);
    const { trackEvent } = useAnalyticsService();
    const searchSuggestions = useGlobalSearchSuggestions(inputValue);
    const continueWatching = useContinueWatching({ limit: 4 });
    const lastSyncedParams = useRef<string | null>(null);
    const hasHydratedFromParams = useRef(false);
    const settingsDialogPointerOperation = useRef<SettingsDialogPointerOperation | null>(null);
    const trackedCollectionKeys = useRef<Set<string>>(new Set());
    const lastTrackedSearchKey = useRef<string | null>(null);
    const collectionParamId = getTrimmedParam(searchParams, COLLECTION_ID_PARAM);
    const collectionParamTitle = getTrimmedParam(searchParams, COLLECTION_TITLE_PARAM);
    const collectionParamTags = normalizeSearchParamTags(searchParams.getAll('tags'));
    const collectionParamTagKey = collectionParamTags.join('|');
    const trimmedInput = inputValue.trim();
    const isInputEmpty = trimmedInput.length === 0;
    const shouldUseContextualDiscovery = trimmedInput.length >= CONTEXTUAL_DISCOVERY_MIN_LENGTH;
    const exploreTags = isInputEmpty
        ? displayTags
        : shouldUseContextualDiscovery
            ? searchSuggestions.tags
            : [];
    const exploreFighters = shouldUseContextualDiscovery ? searchSuggestions.fighters : [];
    const exploreEvents = shouldUseContextualDiscovery ? searchSuggestions.events : [];
    const isExploreLoading = isInputEmpty
        ? showTagSkeletons && !tagLoadFailed
        : shouldUseContextualDiscovery && searchSuggestions.status === 'loading';
    const isExploreError = isInputEmpty
        ? tagLoadFailed
        : shouldUseContextualDiscovery && searchSuggestions.status === 'error';

    useEffect(() => {
        const tagsForContext = collectionParamTagKey.length > 0
            ? collectionParamTagKey.split('|')
            : [];
        const collectionParams = new URLSearchParams();
        if (collectionParamId) {
            collectionParams.set(COLLECTION_ID_PARAM, collectionParamId);
        }
        if (collectionParamTitle) {
            collectionParams.set(COLLECTION_TITLE_PARAM, collectionParamTitle);
        }
        tagsForContext.forEach((tag) => collectionParams.append('tags', tag));

        const fallbackContext = buildFallbackCollectionContext(collectionParams, tagsForContext);
        if (!fallbackContext) {
            setCollectionContext(null);
            return;
        }

        let isMounted = true;
        setCollectionContext(fallbackContext);

        fetchCollections()
            .then((collections) => {
                if (!isMounted) {
                    return;
                }

                const matchedCollection = findCollection(collections ?? [], fallbackContext);
                if (!matchedCollection) {
                    setCollectionContext({ ...fallbackContext, status: 'ready' });
                    return;
                }

                setCollectionContext({
                    id: matchedCollection.id,
                    title: matchedCollection.title,
                    description: matchedCollection.description,
                    imageUrl: matchedCollection.imageUrl,
                    tags: matchedCollection.tags.length > 0 ? matchedCollection.tags : fallbackContext.tags,
                    status: 'ready'
                });
            })
            .catch((error) => {
                if (!isMounted) {
                    return;
                }
                console.error('Collection context fetch failed', { error });
                setCollectionContext({ ...fallbackContext, status: 'error' });
            });

        return () => {
            isMounted = false;
        };
    }, [collectionParamId, collectionParamTitle, collectionParamTagKey]);

    useEffect(() => {
        if (!collectionContext || collectionContext.status === 'loading') {
            return;
        }

        const collectionKey = collectionContext.id || collectionContext.title;
        if (!collectionKey || trackedCollectionKeys.current.has(collectionKey)) {
            return;
        }

        trackedCollectionKeys.current.add(collectionKey);
        void trackEvent({
            eventType: ANALYTICS_EVENT_TYPES.COLLECTION_VIEWED,
            metadata: {
                source: 'video_search',
                collection: collectionContext.title,
                label: collectionContext.title,
                tags: collectionContext.tags.join('|')
            }
        });
    }, [collectionContext, trackEvent]);


    const trackSearch = (query: string, tags: string[], source: string) => {
        const normalizedQuery = query.trim();
        const normalizedTags = tags.map((tag) => tag.trim()).filter((tag) => tag.length > 0);
        const hasQuery = normalizedQuery.length > 0 || normalizedTags.length > 0;
        const searchKey = `${hasQuery ? 'search' : 'empty'}|${normalizedQuery}|${normalizedTags.join('|')}`;
        if (lastTrackedSearchKey.current === searchKey) {
            return;
        }

        lastTrackedSearchKey.current = searchKey;
        const clientEventId = createAnalyticsClientEventId();
        void trackEvent({
            eventType: hasQuery ? ANALYTICS_EVENT_TYPES.SEARCH_PERFORMED : ANALYTICS_EVENT_TYPES.SEARCH_EMPTY,
            clientEventId,
            metadata: {
                source,
                query: normalizedQuery,
                tags: normalizedTags.join('|'),
                collection: collectionContext?.title ?? ''
            }
        });

        if (normalizedQuery.length > 0) {
            trackMarketingConversion({
                kind: 'search',
                eventId: clientEventId,
                searchTerm: normalizedQuery,
                metadata: { source }
            });
        }
    };

    const applySearch = (query: string, tags: string[], options: { preserveCollectionContext?: boolean; source?: string } = {}) => {
        const normalizedQuery = query.trim();
        const normalizedTags = tags.map((tag) => tag.trim()).filter((tag) => tag.length > 0);
        const nextInput = buildSearchInput(normalizedQuery, normalizedTags);
        const shouldTryPreserveCollection = options.preserveCollectionContext ?? true;
        const collectionContextForParams = shouldTryPreserveCollection ? collectionContext : null;
        const nextParams = buildSearchParams(normalizedQuery, normalizedTags, collectionContextForParams, {
            viewMode,
            pageSize,
            sort: sortOrder,
            filters: appliedFilters
        });
        const isKeepingCollection = shouldPreserveCollectionContext(collectionContextForParams, normalizedTags);

        setInputValue(nextInput);
        setSearchTerm(normalizedQuery);
        setActiveSearchTags(normalizedTags);
        if (!isKeepingCollection) {
            setCollectionContext(null);
        }
        lastSyncedParams.current = nextParams.toString();
        setSearchParams(nextParams, { replace: true });
        writeStickySearchState(normalizedQuery, normalizedTags);
        trackSearch(normalizedQuery, normalizedTags, options.source ?? 'video_search');
    };

    const applyBrowseState = (nextBrowseState: BrowseState) => {
        setViewMode(nextBrowseState.viewMode);
        setPageSize(nextBrowseState.pageSize);
        setSortOrder(nextBrowseState.sort);
        setAppliedFilters(nextBrowseState.filters);
        setFilterDraft(nextBrowseState.filters);

        const nextParams = buildSearchParams(parsedInput.query, parsedInput.tags, collectionContext, nextBrowseState);
        lastSyncedParams.current = nextParams.toString();
        setSearchParams(nextParams, { replace: true });
    };

    const applySearchFilters = (nextFilters: SearchFilterState, options: { syncDraft?: boolean } = {}) => {
        const normalizedFilters = sanitizeFilterState(nextFilters);
        if (hasSearchFilterState(normalizedFilters)) {
            void trackEvent({
                eventType: ANALYTICS_EVENT_TYPES.FILTER_APPLIED,
                metadata: {
                    source: 'advanced_search',
                    event: normalizedFilters.event,
                    fighter: normalizedFilters.fighter,
                    venue: normalizedFilters.venue,
                    type: normalizedFilters.type,
                    year: normalizedFilters.year,
                    decade: normalizedFilters.decade
                }
            });
        }
        setAppliedFilters(normalizedFilters);
        if (options.syncDraft ?? true) {
            setFilterDraft(normalizedFilters);
        }

        const nextParams = buildSearchParams(parsedInput.query, parsedInput.tags, collectionContext, {
            viewMode,
            pageSize,
            sort: sortOrder,
            filters: normalizedFilters
        });
        lastSyncedParams.current = nextParams.toString();
        setSearchParams(nextParams, { replace: true });
    };

    const applyFilterDraft = () => {
        applySearchFilters(filterDraft);
        setIsAdvancedSearchOpen(false);
    };

    const applyTagFilter = (tagName: string) => {
        const tagKey = normalizeTagNameKey(tagName);
        const nextTags = selectedTagKeys.has(tagKey)
            ? parsedInput.tags.filter((tag) => normalizeTagNameKey(tag) !== tagKey)
            : [...parsedInput.tags, tagName];

        void trackEvent({
            eventType: ANALYTICS_EVENT_TYPES.TAG_CLICKED,
            metadata: {
                source: collectionContext ? 'collection_context' : 'search_discovery',
                tag: tagName,
                label: tagName
            }
        });
        applySearch(parsedInput.query, nextTags, { source: 'tag_filter' });
    };

    const clearCollectionContext = () => {
        const collectionTagKeys = getTagKeySet(collectionContext?.tags ?? []);
        const remainingTags = collectionTagKeys.size === 0
            ? parsedInput.tags
            : parsedInput.tags.filter((tag) => !collectionTagKeys.has(normalizeTagNameKey(tag)));

        applySearch(parsedInput.query, remainingTags, { preserveCollectionContext: false });
    };

    const openSearchSettings = () => {
        setSettingsDialogFrame(createSettingsDialogFrame());
        setIsAdvancedSearchOpen(true);
    };

    const startSettingsDialogDrag = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!settingsDialogFrame || !isDesktopSettingsViewport()) {
            return;
        }

        const target = event.target;
        if (target instanceof HTMLElement && target.closest('button, input, textarea, select, label, a')) {
            return;
        }

        event.preventDefault();
        settingsDialogPointerOperation.current = {
            mode: 'drag',
            startX: event.clientX,
            startY: event.clientY,
            frame: settingsDialogFrame
        };
        document.body.style.userSelect = 'none';
    };

    const startSettingsDialogResize = (event: React.PointerEvent<HTMLButtonElement>) => {
        if (!settingsDialogFrame || !isDesktopSettingsViewport()) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        settingsDialogPointerOperation.current = {
            mode: 'resize',
            startX: event.clientX,
            startY: event.clientY,
            frame: settingsDialogFrame
        };
        document.body.style.userSelect = 'none';
    };

    useEffect(() => {
        if (!hasHydratedFromParams.current) {
            const currentParams = searchParams.toString();
            lastSyncedParams.current = currentParams;
            hasHydratedFromParams.current = true;
        }

        const handler = setTimeout(() => {
            setSearchTerm(parsedInput.query);
            setActiveSearchTags(parsedInput.tags);

            const nextParams = buildSearchParams(parsedInput.query, parsedInput.tags, collectionContext, {
                viewMode,
                pageSize,
                sort: sortOrder,
                filters: appliedFilters
            });
            const isKeepingCollection = shouldPreserveCollectionContext(collectionContext, parsedInput.tags);
            if (!isKeepingCollection && collectionContext) {
                setCollectionContext(null);
            }

            const nextParamString = nextParams.toString();
            const currentParamString = searchParams.toString();
            if (nextParamString !== currentParamString) {
                lastSyncedParams.current = nextParamString;
                setSearchParams(nextParams, { replace: true });
            }
            writeStickySearchState(parsedInput.query, parsedInput.tags);
            if (parsedInput.query.trim().length > 0 || parsedInput.tags.length > 0) {
                trackSearch(parsedInput.query, parsedInput.tags, 'search_input');
            }
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(handler);
    }, [parsedInput.query, parsedInput.tags, parsedTagKey, searchParams, setSearchParams, collectionContext, viewMode, pageSize, sortOrder, appliedFilters]);

    useEffect(() => {
        const currentParams = searchParams.toString();
        if (lastSyncedParams.current === currentParams) {
            return;
        }

        const query = searchParams.get('q') ?? '';
        const tags = normalizeSearchParamTags(searchParams.getAll('tags'));
        const nextInput = buildSearchInput(query, tags);
        const nextViewMode = parseViewModeParam(searchParams);
        const nextPageSize = parsePageSizeParam(searchParams);
        const nextSortOrder = parseSortParam(searchParams);
        const nextFilters = parseFilterParams(searchParams);
        setInputValue((prev) => (prev === nextInput ? prev : nextInput));
        setViewMode((prev) => (prev === nextViewMode ? prev : nextViewMode));
        setPageSize((prev) => (prev === nextPageSize ? prev : nextPageSize));
        setSortOrder((prev) => (prev === nextSortOrder ? prev : nextSortOrder));
        setAppliedFilters((prev) => (
            JSON.stringify(prev) === JSON.stringify(nextFilters) ? prev : nextFilters
        ));
        setFilterDraft((prev) => (
            JSON.stringify(prev) === JSON.stringify(nextFilters) ? prev : nextFilters
        ));
        lastSyncedParams.current = currentParams;
    }, [searchParams, setInputValue]);

    useEffect(() => {
        if (!isAdvancedSearchOpen) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsAdvancedSearchOpen(false);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isAdvancedSearchOpen]);

    useEffect(() => {
        if (!isAdvancedSearchOpen) {
            return;
        }

        const handleResize = () => {
            setSettingsDialogFrame((current) => (
                current ? clampSettingsDialogFrame(current) : createSettingsDialogFrame()
            ));
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [isAdvancedSearchOpen]);

    useEffect(() => {
        if (!isAdvancedSearchOpen) {
            return;
        }

        const finishPointerOperation = () => {
            settingsDialogPointerOperation.current = null;
            document.body.style.userSelect = '';
        };
        const handlePointerMove = (event: PointerEvent) => {
            const operation = settingsDialogPointerOperation.current;
            if (!operation) {
                return;
            }

            event.preventDefault();
            const deltaX = event.clientX - operation.startX;
            const deltaY = event.clientY - operation.startY;
            const nextFrame = operation.mode === 'drag'
                ? {
                    ...operation.frame,
                    left: operation.frame.left + deltaX,
                    top: operation.frame.top + deltaY
                }
                : {
                    ...operation.frame,
                    width: operation.frame.width + deltaX,
                    height: operation.frame.height + deltaY
                };

            setSettingsDialogFrame(clampSettingsDialogFrame(nextFrame));
        };

        document.addEventListener('pointermove', handlePointerMove);
        document.addEventListener('pointerup', finishPointerOperation);
        document.addEventListener('pointercancel', finishPointerOperation);
        return () => {
            document.removeEventListener('pointermove', handlePointerMove);
            document.removeEventListener('pointerup', finishPointerOperation);
            document.removeEventListener('pointercancel', finishPointerOperation);
            finishPointerOperation();
        };
    }, [isAdvancedSearchOpen]);

    const isSearching = searchTerm.length > 0 || activeSearchTags.length > 0 || hasSearchFilterState(appliedFilters);
    const hasCollectionContext = Boolean(collectionContext);
    const collectionTags = collectionContext?.tags ?? [];
    const collectionDiscoveryTags = collectionTags.map((tag, index) => ({
        id: `collection-${normalizeTagNameKey(tag) || tag}-${index}`,
        name: tag
    }));
    const collectionDetailsText = collectionContext?.description?.trim()
        || (collectionContext?.status === 'loading' ? 'Loading collection details...' : 'Curated fights from this Combat TV collection.');
    const renderCollectionTags = (className: string) => collectionDiscoveryTags.length > 0 ? (
        <div className={className} aria-label="Collection tags">
            {collectionDiscoveryTags.map((tag) => (
                <button
                    key={tag.id}
                    type="button"
                    className={styles.collectionTag}
                    onClick={() => applyTagFilter(tag.name)}
                >
                    #{tag.name}
                </button>
            ))}
        </div>
    ) : null;
    const collectionContextCard = collectionContext ? (
        <section className={styles.collectionContextCard} aria-label={`${collectionContext.title} collection`}>
            <div className={styles.collectionContextMedia}>
                {collectionContext.imageUrl ? (
                    <img src={collectionContext.imageUrl} alt="" loading="lazy" />
                ) : (
                    <div className={styles.collectionContextPlaceholder} aria-hidden="true">
                        <span className="material-symbols-outlined">video_library</span>
                    </div>
                )}
            </div>
            <div className={styles.collectionContextBody}>
                <div className={styles.collectionContextCopy}>
                    <span className={styles.collectionEyebrow}>Collection</span>
                    <h2>{collectionContext.title}</h2>
                    <p>{collectionDetailsText}</p>
                    {collectionContext.status === 'error' ? (
                        <span className={styles.collectionContextWarning}>
                            Live collection details are unavailable. Showing the saved collection filters.
                        </span>
                    ) : null}
                </div>
                <div className={styles.collectionContextActions}>
                    {renderCollectionTags(styles.collectionTagRow)}
                    <button type="button" className={styles.clearCollectionButton} onClick={clearCollectionContext}>
                        Clear collection
                    </button>
                </div>
            </div>
        </section>
    ) : null;
    const desktopCollectionPanel = collectionContext ? (
        <aside className={styles.collectionSidePanel} aria-label="Collection details">
            <div className={styles.collectionSideHeader}>
                <span className={styles.collectionEyebrow}>Collection</span>
                <h3>{collectionContext.title}</h3>
                <p>{collectionDetailsText}</p>
            </div>
            {renderCollectionTags(styles.collectionSideTags)}
            <button type="button" className={styles.clearCollectionButton} onClick={clearCollectionContext}>
                Clear collection
            </button>
        </aside>
    ) : null;
    const collectionEmptyState = collectionContext ? (
        <div className={styles.collectionEmptyState}>
            <span className={styles.collectionEyebrow}>No collection matches</span>
            <h3>No videos found in {collectionContext.title}</h3>
            <p>Try removing a tag, changing the search, or clearing the collection to browse everything.</p>
            <button type="button" className={styles.clearCollectionButton} onClick={clearCollectionContext}>
                Clear collection
            </button>
        </div>
    ) : undefined;
    const buildBrowseState = (overrides: Partial<BrowseState> = {}): BrowseState => ({
        viewMode,
        pageSize,
        sort: sortOrder,
        filters: appliedFilters,
        ...overrides
    });
    const clearAdvancedFilters = () => {
        const clearedFilters = emptySearchFilters();
        const nextParams = buildSearchParams(parsedInput.query, parsedInput.tags, collectionContext, {
            viewMode,
            pageSize,
            sort: sortOrder,
            filters: clearedFilters
        });

        setAppliedFilters(clearedFilters);
        setFilterDraft(clearedFilters);
        lastSyncedParams.current = nextParams.toString();
        setSearchParams(nextParams, { replace: true });
    };
    const updateFilterDraft = (key: keyof SearchFilterState, value: string) => {
        const nextFilters = { ...filterDraft, [key]: value };
        setFilterDraft(nextFilters);
        applySearchFilters(nextFilters, { syncDraft: false });
    };
    const filterFields: Array<{ key: keyof SearchFilterState; label: string; placeholder: string; numeric?: boolean }> = [
        { key: 'fighter', label: 'Fighter', placeholder: 'Name' },
        { key: 'event', label: 'Event', placeholder: 'Event name' },
        { key: 'venue', label: 'Venue', placeholder: 'Venue or city' },
        { key: 'type', label: 'Type', placeholder: 'MMA, boxing...' },
        { key: 'year', label: 'Year', placeholder: '2025', numeric: true },
        { key: 'decade', label: 'Decade', placeholder: '2020', numeric: true }
    ];
    const appliedFilterEntries = (Object.entries(sanitizeFilterState(appliedFilters)) as Array<[keyof SearchFilterState, string]>)
        .filter(([, value]) => value.length > 0);
    const advancedFilterCount = appliedFilterEntries.length;
    const browseSettingBadges = [
        { id: 'sort', label: 'Sort', value: sortOrder === 'oldest' ? 'Oldest' : 'Newest' },
        { id: 'view', label: 'View', value: viewMode === 'compact' ? 'Compact' : 'Grid' },
        { id: 'page-size', label: 'Per page', value: String(pageSize) },
        ...(advancedFilterCount > 0 ? [{ id: 'filters', label: 'Filters', value: String(advancedFilterCount) }] : [])
    ];
    const settingsDialogClassName = settingsDialogFrame
        ? `${styles.advancedDialog} ${styles.advancedDialogFramed}`
        : styles.advancedDialog;
    const settingsDialogStyle: React.CSSProperties | undefined = settingsDialogFrame ? {
        height: settingsDialogFrame.height,
        left: settingsDialogFrame.left,
        top: settingsDialogFrame.top,
        width: settingsDialogFrame.width
    } : undefined;
    const browseTools = (
        <>
            <section className={styles.browseTools} aria-label="Browse tools">
                <button
                    type="button"
                    className={styles.browseSummaryButton}
                    onClick={openSearchSettings}
                    aria-haspopup="dialog"
                >
                    <span className={styles.browseSummaryContent}>
                        <span className={styles.browseSummaryTitle}>
                            <span className="material-symbols-outlined" aria-hidden="true">tune</span>
                            Search settings
                        </span>
                        <span className={styles.browseSummaryBadges} aria-label="Current search settings">
                            {browseSettingBadges.map((badge) => (
                                <span key={badge.id} className={styles.browseSummaryBadge}>
                                    <span>{badge.label}</span>
                                    <strong>{badge.value}</strong>
                                </span>
                            ))}
                        </span>
                    </span>
                    <span className={styles.browseSummaryAction}>
                        <span className={styles.browseSummaryActionText}>Open</span>
                        <span className="material-symbols-outlined" aria-hidden="true">expand_more</span>
                    </span>
                </button>
            </section>

            {isAdvancedSearchOpen ? (
                <div
                    className={styles.advancedOverlay}
                    role="presentation"
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget) {
                            setIsAdvancedSearchOpen(false);
                        }
                    }}
                >
                    <section
                        className={settingsDialogClassName}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="advanced-search-title"
                        style={settingsDialogStyle}
                        onMouseDown={(event) => event.stopPropagation()}
                    >
                        <div className={styles.advancedDialogHeader} onPointerDown={startSettingsDialogDrag}>
                            <div>
                                <span className={styles.filterSummaryEyebrow}>Search tools</span>
                                <h3 id="advanced-search-title">Search settings</h3>
                            </div>
                            <button
                                type="button"
                                className={styles.advancedCloseButton}
                                onClick={() => setIsAdvancedSearchOpen(false)}
                                aria-label="Close search settings"
                            >
                                <span className="material-symbols-outlined" aria-hidden="true">close</span>
                            </button>
                        </div>

                        <div className={styles.overlaySettingsBody}>
                            <section className={styles.overlaySettingsSection} aria-label="Results layout">
                                <div className={styles.overlaySettingsHeader}>
                                    <h4>Results layout</h4>
                                </div>
                                <div className={styles.browseControlGrid}>
                    <div className={styles.controlGroup}>
                        <span className={styles.controlLabel}>Sort</span>
                        <div className={styles.segmentedControl} role="group" aria-label="Results sort">
                            <button
                                type="button"
                                className={sortOrder === 'newest' ? styles.segmentButtonActive : styles.segmentButton}
                                onClick={() => applyBrowseState(buildBrowseState({ sort: 'newest' }))}
                                aria-pressed={sortOrder === 'newest'}
                            >
                                Newest
                            </button>
                            <button
                                type="button"
                                className={sortOrder === 'oldest' ? styles.segmentButtonActive : styles.segmentButton}
                                onClick={() => applyBrowseState(buildBrowseState({ sort: 'oldest' }))}
                                aria-pressed={sortOrder === 'oldest'}
                            >
                                Oldest
                            </button>
                        </div>
                    </div>

                    <div className={styles.controlGroup}>
                        <span className={styles.controlLabel}>View</span>
                        <div className={styles.segmentedControl} role="group" aria-label="Results view">
                            <button
                                type="button"
                                className={viewMode === 'grid' ? styles.segmentButtonActive : styles.segmentButton}
                                onClick={() => applyBrowseState(buildBrowseState({ viewMode: 'grid' }))}
                                aria-pressed={viewMode === 'grid'}
                            >
                                <span className="material-symbols-outlined" aria-hidden="true">grid_view</span>
                                Grid
                            </button>
                            <button
                                type="button"
                                className={viewMode === 'compact' ? styles.segmentButtonActive : styles.segmentButton}
                                onClick={() => applyBrowseState(buildBrowseState({ viewMode: 'compact' }))}
                                aria-pressed={viewMode === 'compact'}
                            >
                                <span className="material-symbols-outlined" aria-hidden="true">view_agenda</span>
                                Compact
                            </button>
                        </div>
                    </div>

                    <div className={styles.controlGroup}>
                        <span className={styles.controlLabel}>Per page</span>
                        <div className={styles.segmentedControl} role="group" aria-label="Results per page">
                            {PAGE_SIZE_OPTIONS.map((option) => (
                                <button
                                    key={option}
                                    type="button"
                                    className={pageSize === option ? styles.segmentButtonActive : styles.segmentButton}
                                    onClick={() => applyBrowseState(buildBrowseState({ pageSize: option }))}
                                    aria-pressed={pageSize === option}
                                >
                                    {option}
                                </button>
                            ))}
                        </div>
                    </div>
                                </div>
                            </section>

                        <form
                            className={styles.advancedFilterForm}
                            onSubmit={(event) => {
                                event.preventDefault();
                                applyFilterDraft();
                            }}
                        >
                            <section className={styles.overlaySettingsSection} aria-label="Advanced search fields">
                                <div className={styles.overlaySettingsHeader}>
                                    <div>
                                        <h4>Match details</h4>
                                        <p>These fields update the results as you type.</p>
                                    </div>
                                    {advancedFilterCount > 0 ? (
                                        <button type="button" className={styles.clearAdvancedButton} onClick={clearAdvancedFilters}>
                                            Clear details
                                        </button>
                                    ) : null}
                                </div>

                            <div className={styles.advancedFilterGrid}>
                                {filterFields.map((field) => (
                                    <label key={field.key} className={styles.filterField}>
                                        <span>{field.label}</span>
                                        <input
                                            type="text"
                                            inputMode={field.numeric ? 'numeric' : 'text'}
                                            value={filterDraft[field.key]}
                                            onChange={(event) => updateFilterDraft(field.key, event.target.value)}
                                            placeholder={field.placeholder}
                                        />
                                    </label>
                                ))}
                            </div>
                            </section>

                            <div className={styles.advancedDialogFooter}>
                                <button type="submit" className={styles.applyFiltersButton}>
                                    Done
                                </button>
                            </div>
                        </form>
                        </div>
                        <button
                            type="button"
                            className={styles.advancedResizeHandle}
                            onPointerDown={startSettingsDialogResize}
                            aria-label="Resize search settings"
                        />
                    </section>
                </div>
            ) : null}
        </>
    );
    const renderDiscoveryContent = () => (
        <SearchDiscoveryContent
            tags={exploreTags}
            fighters={exploreFighters}
            events={exploreEvents}
            isLoading={isExploreLoading}
            isError={isExploreError}
            isBelowMinimum={!isInputEmpty && !shouldUseContextualDiscovery}
            showVideos={false}
            showViewAll={false}
            showNoResultsMessage={!isInputEmpty && shouldUseContextualDiscovery}
            onTagSelect={(tag) => applyTagFilter(tag.name)}
            onFighterSelect={(fighter) => {
                void trackEvent({
                    eventType: ANALYTICS_EVENT_TYPES.FIGHTER_CLICKED,
                    metadata: {
                        source: 'search_discovery',
                        fighter,
                        label: fighter
                    }
                });
                applySearch(fighter, [], { source: 'fighter_suggestion' });
            }}
            onEventSelect={(eventName) => {
                void trackEvent({
                    eventType: ANALYTICS_EVENT_TYPES.EVENT_CLICKED,
                    metadata: {
                        source: 'search_discovery',
                        event: eventName,
                        label: eventName
                    }
                });
                applySearch(eventName, [], { source: 'event_suggestion' });
            }}
            errorTitle={isInputEmpty ? 'Suggested tags are unavailable right now.' : 'Search suggestions are unavailable right now.'}
            emptyMessage="Explore popular tags or search for a fighter, event, or video."
            noResultsMessage={isInputEmpty ? 'No suggested tags available yet.' : 'No matching tags, fighters, or events yet.'}
        />
    );
    const inlineDiscoveryClassName = [
        styles.inlineDiscoveryPanel,
        isSearching ? styles.inlineDiscoveryPanelHiddenWide : ''
    ].filter(Boolean).join(' ');
    const hasContinueWatching = continueWatching.isLoading || continueWatching.items.length > 0;
    const renderContinueWatchingPanel = (className?: string) => hasContinueWatching ? (
        <WatchMomentumPanel
            className={className}
            variant="section"
            continueItems={continueWatching.items}
            isContinueLoading={continueWatching.isLoading}
            continueTitle="Continue Watching"
            continueSubtitle="Resume recent fights without leaving search."
        />
    ) : null;
    const desktopDiscoveryPanel = isSearching ? (
        <aside className={styles.discoveryPanel} aria-label="Search discovery">
            {renderDiscoveryContent()}
        </aside>
    ) : null;
    const desktopContinuePanel = isSearching && hasContinueWatching ? (
        <WatchMomentumPanel
            className={styles.desktopContinuePanel}
            variant="sidebar"
            continueItems={continueWatching.items}
            isContinueLoading={continueWatching.isLoading}
            continueTitle="Continue Watching"
            continueSubtitle="Resume recent fights without leaving search."
        />
    ) : null;
    const topContinuePanel = !isSearching ? renderContinueWatchingPanel(styles.inlineMomentumPanel) : null;
    const bottomContinuePanel = isSearching ? renderContinueWatchingPanel(styles.inlineMomentumPanel) : null;
    const sideRail = desktopCollectionPanel || desktopDiscoveryPanel || desktopContinuePanel ? (
        <aside className={styles.searchSideRail} aria-label="Search support">
            {desktopCollectionPanel}
            {desktopDiscoveryPanel}
            {desktopContinuePanel}
        </aside>
    ) : null;
    const workspaceClassName = [
        styles.searchWorkspace,
        sideRail ? styles.searchWorkspaceWithRail : ''
    ].filter(Boolean).join(' ');

    return (
        <div className={styles.page}>
            <PageHeader title="Find fights" subtitle="Search the latest videos by fighter, event, title, or tag." />

            <div className={workspaceClassName}>
                {sideRail}

                <section className={styles.resultsSection}>
                    <section className={styles.searchModule} aria-label="Search videos">
                        <VideoSearchBar
                            inputId="video-search-input"
                            value={inputValue}
                            onValueChange={setInputValue}
                            onSubmit={() => applySearch(parsedInput.query, parsedInput.tags)}
                            placeholder="Search fighters, events, tags, or videos"
                            showClearButton
                        />
                        <div className={inlineDiscoveryClassName}>
                            {renderDiscoveryContent()}
                        </div>
                    </section>

                    {topContinuePanel}

                    {!isSearching ? <VideoSearchHomeSections mode="idle" /> : null}

                    {collectionContextCard}

                    {browseTools}

                    <div className={styles.resultsHeader}>
                        <div>
                            <h2>{hasCollectionContext ? 'Collection Videos' : 'Results'}</h2>
                            <p>
                                {hasCollectionContext
                                    ? `Ready to play from ${collectionContext?.title}.`
                                    : isSearching
                                        ? 'Ready to play from this search.'
                                        : 'Browse the latest fights and features.'}
                            </p>
                        </div>
                    </div>

                    <div className={styles.resultsInner}>
                        <VideoList
                            pageSize={pageSize}
                            searchTerm={searchTerm}
                            tags={activeSearchTags}
                            viewMode={viewMode}
                            filters={buildVideoSearchFilters(sortOrder, appliedFilters)}
                            allowDefaultLoad
                            centerNoResults={false}
                            emptyState={collectionEmptyState}
                            watchContext={{ query: searchTerm, tags: activeSearchTags }}
                        />
                    </div>

                    {bottomContinuePanel}

                    {isSearching ? <VideoSearchHomeSections mode="searching" /> : null}
                </section>
            </div>
        </div>
    );
};

export default VideoSearch;
